// ────────────────────────────────────────────────────────────────────────────────
// ContikPro Skills — Invoice Intelligence & Financial Analytics
// Part of Tonveon ecosystem skills
// ────────────────────────────────────────────────────────────────────────────────
// ── Virtual CFO ──────────────────────────────────────────────────────────────
export async function askCFO(query: string, rawContextData: string): Promise<string> {
    const systemPrompt = `
Eres el "CFO Virtual" integrado dentro del software ContikPro de tu usuario. 
Eres un experto financiero, irónico pero extremadamente útil y directo.
Respondes a las preguntas del usuario basándote EN EXCLUSIVA en el siguiente contexto de base de datos actual:
=== CONTEXTO BD ===
${rawContextData}
=== FIN CONTEXTO ===

Reglas:
1. Responde de forma clara y concisa (párrafos muy cortos).
2. Usa viñetas para desglosar datos financieros.
3. No inventes datos que no estén en el contexto. Si preguntan algo que no aparece ahí, di que no tienes acceso a esa tabla.
4. Si ves impagos, sugiere cobrar de inmediato. Si ves poco beneficio, dale una colleja amistosa.
    `;
    return callGroqText(systemPrompt, query);
}
// ── Types ────────────────────────────────────────────────────────────────────
import { callGroqText, callGroqOCR } from './groqService';

export interface InvoiceBreakdown {
  subtotal: number;
  irpfRate: number;
  irpfAmount: number;
  ivaRate: number;
  ivaAmount: number;
  reRate: number;
  reAmount: number;
  total: number;
}

export interface ClientRisk {
  clientName: string;
  avgPaymentDays: number;
  overdueInvoices: number;
  totalOwed: number;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  suggestion: string;
}

export interface CashflowMonth {
  month: string;
  expectedIncome: number;
  expectedExpenses: number;
  netCashflow: number;
  cumulativeBalance: number;
}

export interface TaxEstimate {
  quarter: string;
  ivaRepercutido: number;
  ivaSoportado: number;
  resultadoIva: number;
  irpfRetenido: number;
  baseImponibleIrpf: number;
  pagoFraccionado: number;
}

// ── VeriFactu Compliance ─────────────────────────────────────────────────────

/**
 * Genera el hash encadenado requerido por la Ley Antifraude (VeriFactu)
 * Cadena: NIF_Emisor | NumFactura | Fecha | Importe | Hash_Anterior
 */
export async function generateVeriFactuHash(
  issuerNif: string,
  invoiceNum: string,
  issueDate: string,
  totalAmount: number,
  previousHash: string = ''
): Promise<string> {
  const payload = `${issuerNif}|${invoiceNum}|${issueDate}|${totalAmount.toFixed(2)}|${previousHash}`;
  const msgBuffer = new TextEncoder().encode(payload);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
}

// ── Spanish Invoice Calculator ───────────────────────────────────────────────

/** Standard IVA rates in Spain */
export const IVA_RATES = {
  general: 0.21,
  reducido: 0.10,
  superreducido: 0.04,
  exento: 0,
};

/** IRPF retention rates */
export const IRPF_RATES = {
  general: 0.15,
  nuevosAutonomos: 0.07, // First 3 years
  profesional: 0.15,
  modulos: 0.01,
};

/** Calculate Spanish invoice breakdown */
export function calculateInvoice(
  subtotal: number,
  ivaType: keyof typeof IVA_RATES = 'general',
  irpfRate: number = 0.15,
  applyRE: boolean = false // Recargo de Equivalencia
): InvoiceBreakdown {
  const ivaRate = IVA_RATES[ivaType];
  const ivaAmount = subtotal * ivaRate;
  const irpfAmount = subtotal * irpfRate;

  // Recargo de Equivalencia
  const reRates: Record<string, number> = { general: 0.052, reducido: 0.014, superreducido: 0.005, exento: 0 };
  const reRate = applyRE ? (reRates[ivaType] || 0) : 0;
  const reAmount = subtotal * reRate;

  const total = subtotal + ivaAmount + reAmount - irpfAmount;

  return {
    subtotal: round(subtotal),
    irpfRate,
    irpfAmount: round(irpfAmount),
    ivaRate,
    ivaAmount: round(ivaAmount),
    reRate,
    reAmount: round(reAmount),
    total: round(total),
  };
}

/** Bulk calculate multiple invoice lines */
export function calculateMultiLineInvoice(
  lines: Array<{ description: string; quantity: number; unitPrice: number; ivaType?: keyof typeof IVA_RATES }>,
  irpfRate: number = 0.15
): { lines: Array<{ description: string; quantity: number; unitPrice: number; lineTotal: number }>; breakdown: InvoiceBreakdown } {
  const processedLines = lines.map(l => ({
    description: l.description,
    quantity: l.quantity,
    unitPrice: l.unitPrice,
    lineTotal: round(l.quantity * l.unitPrice),
  }));
  const subtotal = processedLines.reduce((s, l) => s + l.lineTotal, 0);
  return {
    lines: processedLines,
    breakdown: calculateInvoice(subtotal, lines[0]?.ivaType || 'general', irpfRate),
  };
}

// ── Client Payment Risk Analyzer ─────────────────────────────────────────────

/** Analyze client payment behavior */
export function analyzeClientPaymentRisk(
  clientName: string,
  invoices: Array<{ amount: number; issuedDate: string; paidDate: string | null; dueDate: string }>
): ClientRisk {
  if (!invoices.length) {
    return { clientName, avgPaymentDays: 0, overdueInvoices: 0, totalOwed: 0, riskLevel: 'low', suggestion: 'Sin historial' };
  }

  const paymentDays: number[] = [];
  let overdueCount = 0;
  let totalOwed = 0;

  for (const inv of invoices) {
    const due = new Date(inv.dueDate).getTime();
    if (inv.paidDate) {
      const paid = new Date(inv.paidDate).getTime();
      const issued = new Date(inv.issuedDate).getTime();
      paymentDays.push(Math.round((paid - issued) / 86400000));
    } else {
      totalOwed += inv.amount;
      if (Date.now() > due) overdueCount++;
    }
  }

  const avgDays = paymentDays.length > 0 ? Math.round(paymentDays.reduce((s, d) => s + d, 0) / paymentDays.length) : 0;

  let riskLevel: ClientRisk['riskLevel'] = 'low';
  let suggestion = 'Buen pagador. Mantener condiciones actuales.';

  if (overdueCount >= 3 || totalOwed > 5000) {
    riskLevel = 'critical';
    suggestion = '⚠️ Riesgo alto. Exigir pago anticipado o reducir límite de crédito.';
  } else if (overdueCount >= 1 || avgDays > 60) {
    riskLevel = 'high';
    suggestion = 'Pagos frecuentemente retrasados. Considerar recargos por demora.';
  } else if (avgDays > 30) {
    riskLevel = 'medium';
    suggestion = 'Pagos dentro de plazos aceptables pero lentos.';
  }

  return { clientName, avgPaymentDays: avgDays, overdueInvoices: overdueCount, totalOwed: round(totalOwed), riskLevel, suggestion };
}

/** Draft a persuasive WhatsApp reminder for an overdue client using LLM */
export async function draftWhatsAppReminder(
  clientName: string,
  totalOwed: number,
  avgDaysOverdue: number,
  riskLevel: ClientRisk['riskLevel']
): Promise<string> {
  const systemPrompt = `
Eres un asistente financiero de autónomos y pymes en España.
Tu tarea es redactar un mensaje corto y directo de WhatsApp para reclamar un pago pendiente a un cliente.

REGLAS:
1. Sé educado pero firme.
2. Si el riesgo es 'low' o 'medium', asume que es un olvido (tono amistoso).
3. Si el riesgo es 'high' o 'critical', el tono debe ser más legal y formal, avisando que el pago está muy retrasado.
4. Incluye la cantidad adeudada (${totalOwed}€).
5. El mensaje debe ser corto (máximo 4-5 líneas).
6. Usa emojis solo si el tono es amistoso.
7. Devuelve SÓLO el texto del mensaje, nada de comillas ni introducciones.
  `;

  const userMessage = `
El cliente "${clientName}" nos debe un total de ${totalOwed}€. 
Lleva un retraso ponderado de ${avgDaysOverdue} días. 
Su nivel de riesgo es catalogado como: ${riskLevel}.
Redacta el mensaje correspondiente ahora.
  `;

  try {
    return await callGroqText(systemPrompt, userMessage);
  } catch (error) {
    console.error('Groq drafting error:', error);
    // Fallback if AI fails or no API key is set
    return `Hola ${clientName}, te escribo para recordarte que tenemos facturas pendientes de pago por un total de ${totalOwed}€. ¿Podrías confirmarme cuándo se realizará la transferencia? Quedo a la espera. Un saludo.`;
  }
}

// ── Sabueso Fiscal (Auditor de Gastos) ───────────────────────────────────────

export interface ExpenseAuditResult {
  isDeductible: boolean;
  confidence: 'high' | 'medium' | 'low';
  redFlags: string[];
  recommendation: string;
}

export async function auditExpense(
  concept: string,
  category: string,
  amount: number,
  hasAttachment: boolean
): Promise<ExpenseAuditResult> {
  const systemPrompt = `
Eres "Sabueso Fiscal", un estricto e irónico inspector de Hacienda en España.
Tu tarea es auditar un gasto registrado por un autónomo/empresa y determinar su deducibilidad.

REGLAS ESTRICTAS DE DEDUCIBILIDAD ESPAÑOLA (AEAT):
1. Dietas/Comidas: Extremadamente vigiladas. Si no hay NIF explícito (o attachment), red flag. Si es fin de semana, red flag.
2. Gasolina/Peajes/Vehículos: Por defecto 50% deducible IVA salvo vehículo 100% industrial. IRPF complicado si no es comercial.
3. Ropa: No deducible salvo EPIs (Equipos de Protección Individual) o uniformes con logo.
4. Atención a clientes: Máximo 1% importe neto de cifra de negocios (generalmente dudoso si es mucho).

FORMATO DE SALIDA DEBE SER JSON ESTRICTO:
{
  "isDeductible": boolean,
  "confidence": "high" | "medium" | "low",
  "redFlags": ["Mención específica de por qué Hacienda podría rechazarlo"],
  "recommendation": "Tu comentario sarcástico pero profesional"
}
  `;

  const userMessage = `
Gasto reportado:
Concepto: ${concept}
Categoría: ${category}
Importe: ${amount}€
¿Tiene ticket escaneado adjunto?: ${hasAttachment ? 'Sí' : 'No'}
  `;

  try {
    const rawContent = await callGroqOCR(systemPrompt, userMessage);
    const result = JSON.parse(rawContent);
    return {
      isDeductible: result.isDeductible ?? false,
      confidence: result.confidence ?? 'low',
      redFlags: result.redFlags || [],
      recommendation: result.recommendation || 'Gasto no evaluable correctamente.',
    };
  } catch (error) {
    console.error('Groq audit error:', error);
    return {
      isDeductible: false,
      confidence: 'low',
      redFlags: ['Error en la conexión con el servidor IA de auditoría.'],
      recommendation: 'Debido a un error interno del Sabueso, asume que no es deducible por precaución.'
    };
  }
}

// ── Cashflow Forecaster ──────────────────────────────────────────────────────

/** Forecast cashflow for the next N months */
export function forecastCashflow(
  currentBalance: number,
  avgMonthlyIncome: number,
  avgMonthlyExpenses: number,
  months: number = 6,
  startMonth: number = new Date().getMonth()
): CashflowMonth[] {
  const MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const seasonalFactors: Record<number, number> = { 0: 0.8, 1: 0.9, 5: 0.7, 6: 0.6, 7: 0.5, 11: 1.2 }; // January slow, summer slow, December high

  let cumulative = currentBalance;
  const result: CashflowMonth[] = [];

  for (let i = 0; i < months; i++) {
    const m = (startMonth + i) % 12;
    const factor = seasonalFactors[m] || 1.0;
    const income = round(avgMonthlyIncome * factor);
    const expenses = round(avgMonthlyExpenses * (factor > 0.8 ? 1.0 : 0.9)); // expenses don't drop as much
    const net = income - expenses;
    cumulative += net;

    result.push({
      month: MONTHS_ES[m],
      expectedIncome: income,
      expectedExpenses: expenses,
      netCashflow: round(net),
      cumulativeBalance: round(cumulative),
    });
  }

  return result;
}

// ── Tax Estimator ────────────────────────────────────────────────────────────

/** Estimate quarterly taxes (Spain autónomo) */
export function estimateQuarterlyTax(
  quarterIncome: number,
  quarterExpenses: number,
  ivaRepercutido: number,
  ivaSoportado: number,
  irpfRetenido: number,
  quarter: 'T1' | 'T2' | 'T3' | 'T4' = 'T1'
): TaxEstimate {
  const resultadoIva = ivaRepercutido - ivaSoportado;
  const baseImponible = quarterIncome - quarterExpenses;
  const pagoFraccionado = Math.max(0, baseImponible * 0.20 - irpfRetenido); // Modelo 130

  return {
    quarter,
    ivaRepercutido: round(ivaRepercutido),
    ivaSoportado: round(ivaSoportado),
    resultadoIva: round(resultadoIva),
    irpfRetenido: round(irpfRetenido),
    baseImponibleIrpf: round(baseImponible),
    pagoFraccionado: round(pagoFraccionado),
  };
}

function round(n: number): number { return Math.round(n * 100) / 100; }
